<template>
  <div>
    <div v-if="$apollo.loading || !event">
      <spinner />
    </div>
    <v-layout v-else class="main-page">
      <div class="sidebar-wrapper">
        <div v-if="!isDesktop" class="sidebar-nav-container">
          <div v-if="!sidebar" class="sidebar-nav">
            <div class="sidebar-nav-title">{{ navigationLabel(navbarName) }}</div>
            <div class="sidebar-nav-img-container">
              <img
                class="sidebar-nav-img"
                src="/assets/sidebar-icons/menu-icon.svg"
                :alt="$adminT('navigation.menu', 'Menu')"
                @click="showSidebar()"
              >
            </div>
          </div>
        </div>
        <v-layout else class="sidebar-container">
          <div v-if="sidebar" class="sidebar-holder">
            <div
              class="sidebar-header"
              :style="`background-color: ${currentEventState.backgroundColor};`"
            >
              <div class="sidebar-header-back">
                <button class="go-back-container" @click="goBack()">
                  <v-icon class="back-icon" size="24" color="#0277BD"> arrow_back</v-icon>
                </button>
                <v-spacer />
                <event-status-label :admin-context="true" :status="currentEventState" />
                <v-icon
                  v-if="!isDesktop && sidebar"
                  color="#333333"
                  style="margin-left: 3.35rem"
                  @click="showSidebar()"
                >
                  close
                </v-icon>
              </div>
              <div
                class="sidebar-header-title"
                :style="organizationType === OrganizationType.THEATER && 'margin-bottom: 1rem'"
              >
                {{ event.title }}
              </div>
              <div v-if="dateAndTime" class="sidebar-header-subtitle">
                {{ localizedDateAndTime }} @
                {{ location }}
              </div>
              <div
                v-if="event.eventCode && ![EventState.CANCELED.key].includes(currentEventState.key)"
                class="scanner-code-container"
              >
                <img
                  src="/assets/barcode-scanned.svg"
                  :alt="$adminT('navigation.barcode_icon', 'Barcode icon')"
                  style="width: 24px; height: 24px; margin-right: 6px"
                >
                <span>{{ $adminT('navigation.code', 'Code') }}</span>
                <v-icon
                  color="#0277BD"
                  size="24"
                  style="padding: 0.5rem"
                  @click="openScannerCodeModal = true"
                >
                  info_outline
                </v-icon>
                <event-code-label :code="event.eventCode" />
              </div>
              <template v-if="!isOrganizationEventViewer">
                <div
                  v-if="
                    organizationType !== OrganizationType.THEATER &&
                      currentEventState === EventState.NO_MORE_TICKETS
                  "
                  class="add-more-tickets-container"
                >
                  <p>{{ $adminT('navigation.there_is_no_more_available_tickets', 'There is no more available tickets!') }}</p>
                  <main-button @click="onAddMoreTicketsClick">{{ $adminT('navigation.add_more_tickets_1107', 'ADD MORE TICKETS') }}</main-button>
                </div>
                <main-button
                  v-if="
                    eventId &&
                      me.flags &&
                      me.flags.allowPublishEventsToLanding &&
                      currentEventState === EventState.CANCELED &&
                      organizationType !== OrganizationType.THEATER
                  "
                  color="errorDark"
                  style="margin: 0 0 1rem 0"
                  @click="onSendMessageToAttendeesClick"
                >
                  {{ $adminT('navigation.send_message_to_attendees', 'Send message to attendees') }}
                </main-button>
                <div class="share-change-status-container">
                  <main-button
                    v-if="
                      organizationType !== OrganizationType.THEATER &&
                        currentEventState.key === EventState.DRAFT.key
                    "
                    flat
                    style="background-color: white; margin: 0; height: 3rem"
                    :href="`/organizations/${organizationSlug}/events/${eventId}/preview`"
                    target="_blank"
                  >
                    {{ $adminT('navigation.preview', 'Preview') }}
                  </main-button>
                  <button
                    v-else-if="organizationType !== OrganizationType.THEATER"
                    class="go-back-container"
                    @click="openShareEventModal = true"
                  >
                    <v-icon class="back-icon" size="24" color="#0277BD"> share</v-icon>
                  </button>
                  <main-button
                    v-if="
                      organizationType !== OrganizationType.THEATER &&
                        currentEventState.key === EventState.CANCELED.key
                    "
                    flat
                    style="background-color: white; height: 3rem; margin: 0"
                    @click="openModal = true"
                  >
                    {{ $adminT('navigation.edit_notification', 'Edit notification') }}
                  </main-button>

                  <main-button
                    v-if="
                      organizationType === OrganizationType.THEATER &&
                        currentEventState === EventState.DRAFT &&
                        me.flags &&
                        me.flags.allowPublishEventsToLanding
                    "
                    class="sidebar-view-event-btn"
                    :loading="publishingInProgress"
                    @click="theaterPublishClick"
                  >
                    <div class="publish-page">
                      <span>{{ $adminT('navigation.publish', 'Publish') }}</span>
                      <img
                        class="publish-icon"
                        src="/assets/sidebar-icons/publish.svg"
                        :alt="$adminT('navigation.publish_event_page', 'Publish event page')"
                      >
                    </div>
                  </main-button>
                  <main-button
                    v-else-if="
                      organizationType === OrganizationType.THEATER &&
                        currentEventState === EventState.PUBLISHED
                    "
                    class="share-event-page-button"
                    flat
                    @click="openShareEventModal = true"
                  >
                    <v-icon style="margin-right: 0.5rem">share</v-icon>
                    {{ $adminT('navigation.share_view_event_page', 'Share & view Event page') }}
                  </main-button>

                  <div
                    v-else-if="
                      organizationType !== OrganizationType.THEATER && changeEventStatusOptions.length
                    "
                    ref="changeStatusRef"
                    class="status-change-button"
                  >
                    <main-button
                      style="margin: 0; box-shadow: none"
                      :flat="changeEventStatusLabel === 'Change status'"
                      :class="[
                        'change-status-label',
                        {
                          'change-status-label--selected': changeEventStatusLabel !== 'Change status',
                        },
                      ]"
                      :loading="publishingInProgress"
                      @click="onChangeEventStatusLabelClick"
                    >
                      {{ navigationLabel(changeEventStatusLabel) }}
                    </main-button>
                    <v-menu
                      v-model="eventStatusMenuOpen"
                      offset-y
                      :class="[
                        'change-status-expand',
                        {
                          'change-status-expand--selected':
                            changeEventStatusLabel !== 'Change status',
                        },
                      ]"
                      attach=".status-change-button"
                      nudge-bottom="24"
                      :disabled="publishingInProgress"
                    >
                      <v-icon
                        slot="activator"
                        class="expand-more-icon"
                        :color="changeEventStatusLabel !== 'Change status' ? 'white' : 'primary'"
                      >
                        expand_more
                      </v-icon>
                      <v-list>
                        <v-list-tile
                          v-for="(item, index) in changeEventStatusOptions"
                          :key="index"
                          @click="onChangeEventStatus(item)"
                        >
                          <v-icon
                            v-if="item === EventStatusOptions.PUBLISH"
                            color="primary"
                            style="margin-right: 0.5rem"
                          >
                            publish
                          </v-icon>
                          <v-icon
                            v-else-if="
                              [
                                EventStatusOptions.SCHEDULE_PUBLISH,
                                EventStatusOptions.RESCHEDULE,
                              ].includes(item)
                            "
                            color="primary"
                            style="margin-right: 0.5rem"
                          >
                            calendar_month
                          </v-icon>
                          <v-icon
                            v-else-if="item === EventStatusOptions.CANCEL_EVENT"
                            color="primary"
                            style="margin-right: 0.5rem"
                          >
                            event_busy
                          </v-icon>
                          <v-icon
                            v-else-if="item === EventStatusOptions.ADD_MORE_TICKETS"
                            color="primary"
                            style="margin-right: 0.5rem"
                          >
                            add
                          </v-icon>
                          <v-icon
                            v-else-if="
                              [
                                EventStatusOptions.ACTIVATE_SALES,
                                EventStatusOptions.MARK_AS_AVAILABLE,
                              ].includes(item)
                            "
                            color="primary"
                            style="margin-right: 0.5rem"
                          >
                            confirmation_number
                          </v-icon>
                          <img
                            v-else-if="
                              [
                                EventStatusOptions.PAUSE_SALES,
                                EventStatusOptions.MARK_AS_CLOSED,
                              ].includes(item)
                            "
                            src="/assets/sidebar-icons/tickets-unavailable.svg"
                            :alt="$adminT('navigation.pause_sales', 'Pause sales')"
                            style="margin-right: 0.5rem"
                          >
                          <img
                            v-if="item === EventStatusOptions.MARK_AS_SOLD_OUT"
                            src="/assets/sidebar-icons/tickets-sold-out.svg"
                            :alt="$adminT('navigation.mark_as_sold_out_1481', 'Mark as sold out')"
                            style="margin-right: 0.5rem"
                          >
                          <v-list-tile-title style="color: #0277bd" v-text="navigationLabel(item)" />
                        </v-list-tile>
                      </v-list>
                    </v-menu>
                  </div>
                  <main-button
                    v-else-if="
                      organizationType !== OrganizationType.THEATER &&
                        !isTicketingEnabled &&
                        [EventState.PUBLISHED.key, EventState.SALES_PAUSED.key].includes(
                          currentEventState.key,
                        )
                    "
                    :style="[
                      { height: '3rem', margin: '0' },
                      changeEventStatusLabel === EventStatusOptions.MARK_AS_CLOSED && {
                        'background-color': 'white',
                      },
                    ]"
                    :flat="changeEventStatusLabel === EventStatusOptions.MARK_AS_CLOSED"
                    @click="onChangeEventStatus(changeEventStatusLabel)"
                  >
                    {{ navigationLabel(changeEventStatusLabel) }}
                  </main-button>
                </div>
              </template>
            </div>
            <sidebar :sidebar-config="sidebarConfig" :request-count="requestCount" />
          </div>
        </v-layout>
      </div>
      <v-flex v-show="isDesktop || (!isDesktop && !sidebar)" class="screens">
        <router-view :user="me" :event="event" />
      </v-flex>
      <simple-snackbar :open.sync="snackbar" :message-prop="localizedMessage" :color="color" />
    </v-layout>
    <cancellation-notification-modal
      v-if="openModal"
      :event-cancellation-notification="event.cancellationNotification"
      @closeModal="openModal = false"
      @values="event.cancellationNotification = $event"
      @submit="updateCancellationNotification"
    />
    <scanner-code-modal v-if="openScannerCodeModal" @close="openScannerCodeModal = false" />
    <share-event-modal
      v-if="openShareEventModal"
      :share-options="shareEventOptions"
      :event-title="event.title"
      @close="openShareEventModal = false"
    />
    <add-event-cover-modal
      v-if="openAddEventCoverModal"
      :initial-cover="event.cover"
      @close="openAddEventCoverModal = false"
      @submit="updateEventCover"
    />
    <schedule-publish-modal
      v-if="openSchedulePublishModal"
      :initial-value="event.scheduledPublishDate"
      @close="openSchedulePublishModal = false"
      @schedule="onSchedulePublish"
    />
    <div />
  </div>
</template>

<script>
import AdminContext from '@/admin-i18n/context'
import { navigationLabel } from '../navigation-labels'
import Sidebar from './../Sidebar.vue'
import {
  EVENT_AVAILABLE,
  EVENT_CANCELED,
  EVENT_SOLD_OUT,
  GET_EVENT_AND_LOGGED_USER,
  PUBLISH_EVENT,
  UPDATE_CANCELLATION_NOTIFICATION,
  UPDATE_EVENT_COVER,
  UPDATE_EVENT_SCHEDULED_PUBLISH_DATE,
} from '@/graphql/event.graphql'
import {
  GET_ORGANIZATION_EVENT_TEMPLATE_AND_LOGGED_USER,
  UPDATE_EVENT_TEMPLATE_COVER,
} from '@/graphql/eventTemplate.graphql'
import { EventState, EventStatus, OrganizationType, OrganizerSidebarRouteKeys } from '@/types'
import { cloneDeep, intersection, keys, minBy, pickBy } from 'lodash'
import { organizerSidebarConfiguration } from './organizer-sidebar-configuration'
import { theaterOrganizerSidebarConfiguration } from './theater-organizer-sidebar-configuration'
import moment from 'moment'
import SimpleSnackbar from '../../../shared/snackbars/simple-snackbar.vue'
import CancellationNotificationModal from './cancellation-notification-modal'
import { toggleEventApplications, getAuthorization } from '../../../../http.js'
import { sportOrganizerSidebarConfiguration } from '@/components/shared/sidebars/organizer/sport-organizer-sidebar-configuration'
import EventStatusLabel from '@/components/shared/event-status-label.vue'
import ScannerCodeModal from '@/components/shared/sidebars/organizer/scanner-code-modal.vue'
import ShareEventModal from '@/components/shared/sidebars/organizer/share-event-modal.vue'
import AddEventCoverModal from '@/components/shared/sidebars/organizer/add-event-cover-modal.vue'
import SchedulePublishModal from '@/components/shared/sidebars/organizer/schedule-publish-modal.vue'
import EventCodeLabel from '@/components/shared/event-code-label.vue'

const organizerSidebars = Object.freeze({
  SPORT_ORGANIZATION: sportOrganizerSidebarConfiguration,
  REGULAR: organizerSidebarConfiguration,
  THEATER: theaterOrganizerSidebarConfiguration,
})

const EventStatusOptions = Object.freeze({
  PUBLISH: 'Publish',
  SCHEDULE_PUBLISH: 'Schedule publish',
  RESCHEDULE: 'Reschedule',
  PAUSE_SALES: 'Pause sales',
  MARK_AS_SOLD_OUT: 'Mark as "Sold out"',
  CANCEL_EVENT: 'Cancel event',
  ADD_MORE_TICKETS: 'Add more tickets',
  ACTIVATE_SALES: 'Activate sales',
  MARK_AS_CLOSED: 'Mark as closed',
  MARK_AS_AVAILABLE: 'Mark as available',
})

export default {
  mixins: [AdminContext],
  name: 'organizer-sidebar',
  components: {
    SchedulePublishModal,
    AddEventCoverModal,
    ShareEventModal,
    ScannerCodeModal,
    EventStatusLabel,
    Sidebar,
    SimpleSnackbar,
    CancellationNotificationModal,
    EventCodeLabel,
  },
  props: {
    eventId: {
      default: null,
      type: String,
    },
    eventTemplateId: {
      default: null,
      type: String,
    },
    organizationSlug: {
      type: String,
    },
  },

  data() {
    return {
      sidebarConfig: [],
      sidebarConfigurationByType: [],
      requestCount: this.requestCount,
      event: null,
      location: this.location,
      organizationType: OrganizationType.REGULAR,
      OrganizationType,
      dateAndTime: '',
      me: {},
      sidebar: window.innerWidth > 800,
      isDesktop: window.innerWidth > 800,
      navbarName: this.$router.history.current.meta.title,
      moment,
      menuOpen: false,
      snackbar: false,
      message: '',
      color: '',
      openModal: false,
      openScannerCodeModal: false,
      openShareEventModal: false,
      openAddEventCoverModal: false,
      openSchedulePublishModal: false,
      EventStatusOptions,
      eventStatusMenuOpen: false,
      isTicketingEnabled: false,
      publishingInProgress: false,
      organizationRole: null,
    }
  },
  computed: {
    localizedDateAndTime() {
      const lan = this.isAdminLocalization ? this.$adminLocale() : 'en'

      return moment(this.dateAndTime).locale(lan).format(`MMM DD, YYYY ${lan === 'hr' ? '[u]' : '[at]'} HH:mm`)
    },
    localizedMessage() {
      switch (this.message) {
      case 'navigation.error_in_publishing_event':
        return this.$adminT('navigation.error_in_publishing_event', 'Error in publishing event.')
      case 'navigation.publish_success':
        return this.$adminT('navigation.publish_success', 'The page is successfully published. It will be live in 20 minutes.')
      case 'navigation.sold_out_success':
        return this.$adminT('navigation.sold_out_success', 'The event has been successfully marked as sold out.')
      case 'navigation.action_failed':
        return this.$adminT('navigation.action_failed', 'Unable to update the event. Please try again.')
      case 'navigation.available_success':
        return this.$adminT('navigation.available_success', 'The event has been successfully marked as available.')
      case 'navigation.cancel_success':
        return this.$adminT('navigation.cancel_success', 'The event has been successfully marked as canceled.')
      case 'navigation.unavailable_success':
        return this.$adminT('navigation.unavailable_success', 'The event has been successfully marked as temporarily unavailable.')
      case 'navigation.notification_success':
        return this.$adminT('navigation.notification_success', 'Notification has been successfully published on event page.')
      case 'navigation.error_in_updating_event_cover':
        return this.$adminT('navigation.error_in_updating_event_cover', 'Error in updating event cover.')
      case 'navigation.cover_successfully_updated':
        return this.$adminT('navigation.cover_successfully_updated', 'Cover successfully updated.')
      case 'navigation.error_in_scheduling_publish':
        return this.$adminT('navigation.error_in_scheduling_publish', 'Error in scheduling publish.')
      case 'navigation.schedule_success':
        return this.$adminT('navigation.schedule_success', 'Publish successfully scheduled for selected date.')
      default:
        return ''
      }
    },
    EventState() {
      return EventState
    },
    currentEventState() {
      if (this.organizationType === OrganizationType.THEATER) {
        return EventState[this.event.status === 'PUBLISHED' ? 'PUBLISHED' : 'DRAFT']
      }

      if (!this.isTicketingEnabled) {
        if (this.event.scheduledPublishDate) {
          return EventState.SCHEDULED
        } else if (this.event.status !== 'PUBLISHED') {
          return EventState.DRAFT
        } else if (!this.event.applicationsOpen) {
          return EventState.SALES_PAUSED
        } else {
          return EventState.PUBLISHED
        }
      }

      if (this.event.canceled) {
        return EventState.CANCELED
      } else if (this.event.soldOut) {
        return EventState.SOLD_OUT
      } else if (!this.event.applicationsOpen) {
        return EventState.SALES_PAUSED
      } else if (this.event.venueId && !this.event.hasPurchasableTickets) {
        return EventState.SOLD_OUT
      } else if (this.event.status === 'PUBLISHED' && !this.event.hasPurchasableTickets) {
        return EventState.NO_MORE_TICKETS
      } else if (this.event.status === 'PUBLISHED') {
        return EventState.PUBLISHED
      } else if (this.event.scheduledPublishDate) {
        return EventState.SCHEDULED
      } else {
        return EventState.DRAFT
      }
    },
    changeEventStatusLabel() {
      if (!this.isTicketingEnabled) {
        if ([EventState.DRAFT.key, EventState.SCHEDULED.key].includes(this.currentEventState.key)) {
          return EventStatusOptions.PUBLISH
        } else if (this.currentEventState.key === EventState.PUBLISHED.key) {
          return EventStatusOptions.MARK_AS_CLOSED
        } else if (this.currentEventState.key === EventState.SALES_PAUSED.key) {
          return EventStatusOptions.MARK_AS_AVAILABLE
        }
      }

      if ([EventState.DRAFT.key, EventState.SCHEDULED.key].includes(this.currentEventState.key)) {
        return EventStatusOptions.PUBLISH
      } else if (this.currentEventState.key === EventState.SALES_PAUSED.key) {
        return EventStatusOptions.ACTIVATE_SALES
      } else if (this.currentEventState.key === EventState.NO_MORE_TICKETS.key) {
        return EventStatusOptions.MARK_AS_SOLD_OUT
      }

      return 'Change status'
    },
    changeEventStatusOptions() {
      const isPublishingAllowed = this.me.flags && this.me.flags.allowPublishEventsToLanding
      let options = {}
      if (!this.isTicketingEnabled) {
        options = {
          [EventState.DRAFT.key]: isPublishingAllowed
            ? [
              EventStatusOptions.SCHEDULE_PUBLISH,
              this.event.applicationsOpen
                ? EventStatusOptions.MARK_AS_CLOSED
                : EventStatusOptions.MARK_AS_AVAILABLE,
            ]
            : [],
          [EventState.SCHEDULED.key]: isPublishingAllowed
            ? [
              EventStatusOptions.RESCHEDULE,
              this.event.applicationsOpen
                ? EventStatusOptions.MARK_AS_CLOSED
                : EventStatusOptions.MARK_AS_AVAILABLE,
            ]
            : [],
          [EventState.PUBLISHED.key]: [],
          [EventState.SALES_PAUSED.key]: [],
        }
      } else {
        options = {
          [EventState.DRAFT.key]: isPublishingAllowed ? [EventStatusOptions.SCHEDULE_PUBLISH] : [],
          [EventState.SCHEDULED.key]: isPublishingAllowed ? [EventStatusOptions.RESCHEDULE] : [],
          [EventState.PUBLISHED.key]: [
            EventStatusOptions.PAUSE_SALES,
            EventStatusOptions.MARK_AS_SOLD_OUT,
            EventStatusOptions.CANCEL_EVENT,
          ],
          [EventState.SALES_PAUSED.key]: [
            EventStatusOptions.MARK_AS_SOLD_OUT,
            EventStatusOptions.CANCEL_EVENT,
          ],
          [EventState.NO_MORE_TICKETS.key]: [EventStatusOptions.CANCEL_EVENT],
          [EventState.CANCELED.key]: [],
          [EventState.SOLD_OUT.key]: [
            EventStatusOptions.ADD_MORE_TICKETS,
            ...(this.event.soldOut ? [EventStatusOptions.ACTIVATE_SALES] : []),
            EventStatusOptions.CANCEL_EVENT,
          ],
        }
      }

      return options[this.currentEventState.key]
    },
    isOrganizationEventViewer() {
      return this.organizationRole === 'EVENT_VIEWER'
    },
    shareEventOptions() {
      if (!this.event) {
        return []
      }

      return [
        {
          label: this.$adminT('navigation.english_link', 'English link'),
          url: `${process.env.VUE_APP_LANDING_URL}/en/events/${this.event.slug}`,
          iconSrc: '/assets/flags/language/en.svg',
        },
        {
          label: this.$adminT('navigation.croatian_link', 'Croatian link'),
          url: `${process.env.VUE_APP_LANDING_URL}/events/${this.event.slug}`,
          iconSrc: '/assets/flags/language/hr.svg',
        },
      ]
    },
  },
  watch: {
    $route() {
      this.navbarName = this.$router.history.current.meta.title
      this.sidebar = this.isDesktop
    },
  },
  async mounted() {
    await this.fetchEventData()
    this.isTicketingEnabled = this.event.extraModules && this.event.extraModules.ticketing

    const auth = await getAuthorization(this.eventId, this.organizationSlug, this.eventTemplateId)
    this.organizationRole = auth.organizationRole

    this.sidebarConfig = this.calculateSidebarConfig()
  },
  created() {
    window.addEventListener('resize', this.resizeHandler)
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeHandler)
  },
  methods: {
    navigationLabel,
    async fetchEventData() {
      if (this.eventId) {
        await this.fetchEventAndLoggedUser()
      } else {
        await this.fetchEventTemplateAndLoggedUser()
      }
    },
    containsExtraModule(route) {
      const eventExtraModules = keys(pickBy(this.event.extraModules))
      if (
        route.key === OrganizerSidebarRouteKeys.ACCREDITATION_BUILDER &&
        this.organizationType === this.OrganizationType.THEATER &&
        this.event.extraModules.ticketing
      ) {
        return false
      }

      // Array of event IDs that should still show the pendings menu
      const eventIdsWithPendingRequestMenuOverride = [3239, 9108] // INS 2025, INS 2027
      if (
        route.key === OrganizerSidebarRouteKeys.PENDING_REQUESTS &&
        this.event.extraModules.ticketing &&
        !eventIdsWithPendingRequestMenuOverride.includes(Number(this.event.id))
      ) {
        return false
      }

      return route.modulesRequired
        ? intersection(route.modulesRequired, eventExtraModules).length > 0
        : true
    },
    hasUserRoles(route) {
      return route.userType ? route.userType.includes(this.me.type) : true
    },
    hasOrganizationRole(route) {
      if (!route.allowedOrganizationRoles) return true

      return this.organizationRole
        ? route.allowedOrganizationRoles.includes(this.organizationRole)
        : true
    },
    isVerifiedByCoreEvent(route) {
      return route.checkIfVerifiedByCoreEvent ? this.me.isVerifiedByCoreEvent : true
    },
    shouldRender(route) {
      return (
        this.containsExtraModule(route, this.event) &&
        this.hasUserRoles(route, this.me) &&
        this.hasOrganizationRole(route) &&
        this.isVerifiedByCoreEvent(route, this.me)
      )
    },
    resizeHandler() {
      if (window.innerWidth > 800) {
        this.sidebar = true
        this.isDesktop = true
      } else {
        if (this.isDesktop === true) {
          this.sidebar = false
        }
        this.isDesktop = false
      }
    },

    goToPublicPage() {
      window.open(`${process.env.VUE_APP_LANDING_URL}/events/${this.event.slug}`)
    },

    async publishEventPage() {
      this.publishingInProgress = true
      const { errors } = await this.$apollo.mutate({
        mutation: PUBLISH_EVENT,
        variables: {
          id: this.eventId,
          eventTemplateId: this.eventTemplateId,
        },
      })
      this.publishingInProgress = false
      if (errors) {
        this.message = 'navigation.error_in_publishing_event'
        this.color = 'error'
        this.snackbar = true

        return
      }

      await this.fetchEventData()
      this.message = 'navigation.publish_success'
      this.color = 'primary'
      this.snackbar = true
    },
    publishEventInBackground() {
      this.$apollo.mutate({
        mutation: PUBLISH_EVENT,
        variables: {
          id: this.eventId,
          eventTemplateId: this.eventTemplateId,
        },
      })
    },
    async flagSoldOut() {
      if (!this.event.soldOut) this.menuOpen = false
      const { data, errors } = await this.$apollo.mutate({
        mutation: EVENT_SOLD_OUT,
        refetchQueries: ['GetOrderItems'],
        awaitRefetchQueries: true,
        variables: {
          id: this.eventId,
          organizationSlug: this.organizationSlug,
        },
      })
      if (data) {
        if (this.event.status === EventStatus.PUBLISHED) {
          this.publishEventInBackground()
        }
        this.event.soldOut = data.eventSoldOut.soldOut
        this.message = 'navigation.sold_out_success'
        this.color = 'primary'
        this.snackbar = true
      } else {
        this.message = 'navigation.action_failed'
        this.color = 'error'
        this.snackbar = true
      }
    },
    async markAsAvailable() {
      const { data, errors } = await this.$apollo.mutate({
        mutation: EVENT_AVAILABLE,
        variables: {
          id: this.eventId,
          organizationSlug: this.organizationSlug,
        },
      })
      if (data) {
        if (this.event.status === EventStatus.PUBLISHED) {
          this.publishEventInBackground()
        }
        this.event.soldOut = false
        this.event.applicationsOpen = true
        this.message = 'navigation.available_success'
        this.color = 'primary'
        this.snackbar = true
      } else {
        this.message = 'navigation.action_failed'
        this.color = 'error'
        this.snackbar = true
      }
    },
    async flagCanceled() {
      this.menuOpen = false
      const { data, errors } = await this.$apollo.mutate({
        mutation: EVENT_CANCELED,
        variables: {
          id: this.eventId,
          organizationSlug: this.organizationSlug,
        },
      })
      if (data) {
        if (this.event.status === EventStatus.PUBLISHED) {
          this.publishEventInBackground()
        }
        this.event.canceled = true
        this.message = 'navigation.cancel_success'
        this.color = 'primary'
        this.snackbar = true
      } else {
        this.message = 'navigation.action_failed'
        this.color = 'error'
        this.snackbar = true
      }
    },
    async flagTempUnavailable() {
      this.menuOpen = false
      const { data, errors } = await toggleEventApplications({
        eventId: this.eventId,
        applicationsOpen: false,
      })
      if (data) {
        if (this.event.status === EventStatus.PUBLISHED) {
          this.publishEventInBackground()
        }
        this.event.applicationsOpen = false
        this.message = 'navigation.unavailable_success'
        this.color = 'primary'
        this.snackbar = true
      } else {
        this.message = 'navigation.action_failed'
        this.color = 'error'
        this.snackbar = true
      }
    },
    async updateCancellationNotification() {
      this.openModal = false
      const { data, errors } = await this.$apollo.mutate({
        mutation: UPDATE_CANCELLATION_NOTIFICATION,
        variables: {
          id: this.eventId,
          organizationSlug: this.organizationSlug,
          notification: this.event.cancellationNotification,
        },
      })
      if (data) {
        this.message = 'navigation.notification_success'
        this.color = 'primary'
        this.snackbar = true
      } else {
        this.message = 'navigation.action_failed'
        this.color = 'error'
        this.snackbar = true
      }
    },
    closeOverlay(e) {
      let elClass = e.srcElement.className
      if (this.menuOpen && elClass && elClass != 'more-icon' && elClass != 'more-options-button') {
        this.menuOpen = false
      }
    },
    showSidebar() {
      this.sidebar = !this.sidebar
    },
    goBack() {
      if (this.organizationType === this.OrganizationType.THEATER) {
        this.$router.push('/theater-organizations/' + this.organizationSlug)
      } else if (this.organizationType === this.OrganizationType.SPORT_ORGANIZATION) {
        this.$router.push('/sport-organizations/' + this.organizationSlug)
      } else {
        this.$router.push('/organizations/' + this.organizationSlug)
      }
    },
    async fetchEventAndLoggedUser() {
      const { data, errors } = await this.$apollo.query({
        query: GET_EVENT_AND_LOGGED_USER,
        variables: {
          id: this.eventId,
        },
      })

      if (errors) {
        this.$router.push('/')

        return
      }

      this.me = data.me
      this.event = data.getEvent
      this.requestCount = data.getPendingRequestsCount
      this.dateAndTime = moment(data.getEvent.from).local('en')
      this.location = data.getEvent.location.venue
      this.organizationType = data.getEventOrganizationType

      const sidebarConfiguration = organizerSidebars[this.organizationType]

      this.sidebarConfigurationByType = cloneDeep(sidebarConfiguration)
    },
    async fetchEventTemplateAndLoggedUser() {
      const { data, errors } = await this.$apollo.query({
        query: GET_ORGANIZATION_EVENT_TEMPLATE_AND_LOGGED_USER,
        variables: {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.eventTemplateId,
        },
      })
      if (errors) {
        this.$router.push('/')

        return
      }
      const extraModules = data.getOrganizationEventTemplate.events[0].extraModules
      const status = data.getOrganizationEventTemplate.events.length
        ? data.getOrganizationEventTemplate.events[0].status
        : EventStatus.UNPUBLISHED
      this.me = data.me
      this.event = {
        ...data.getOrganizationEventTemplate,
        extraModules,
        status,
        slug: this.findPerformanceSlugWithEarliestFromDate(
          data.getOrganizationEventTemplate.events,
        ),
      }
      this.organizationType = this.OrganizationType.THEATER

      this.sidebarConfigurationByType = cloneDeep(theaterOrganizerSidebarConfiguration)
    },
    calculateSidebarConfig() {
      return this.sidebarConfigurationByType
        .filter(route => this.shouldRender(route))
        .map(route => {
          if (route.subroutes) {
            route.subroutes = route.subroutes.filter(subroute => this.shouldRender(subroute))
          }

          return route
        })
    },
    onChangeEventStatus(val) {
      if (
        [
          EventStatusOptions.PUBLISH,
          EventStatusOptions.SCHEDULE_PUBLISH,
          EventStatusOptions.RESCHEDULE,
        ].includes(val) &&
        !this.event.cover
      ) {
        this.openAddEventCoverModal = true
      } else if (
        [EventStatusOptions.PAUSE_SALES, EventStatusOptions.MARK_AS_CLOSED].includes(val)
      ) {
        this.flagTempUnavailable()
      } else if (val === EventStatusOptions.MARK_AS_SOLD_OUT) {
        this.flagSoldOut()
      } else if (val === EventStatusOptions.CANCEL_EVENT) {
        this.flagCanceled()
      } else if (val === EventStatusOptions.PUBLISH) {
        this.publishEventPage()
      } else if (val === EventStatusOptions.ADD_MORE_TICKETS) {
        this.onAddMoreTicketsClick()
      } else if (
        [EventStatusOptions.ACTIVATE_SALES, EventStatusOptions.MARK_AS_AVAILABLE].includes(val)
      ) {
        this.markAsAvailable()
      } else if (
        [EventStatusOptions.RESCHEDULE, EventStatusOptions.SCHEDULE_PUBLISH].includes(val)
      ) {
        this.openSchedulePublishModal = true
      }
    },

    onAddMoreTicketsClick() {
      const routeName =
        this.organizationType === OrganizationType.SPORT_ORGANIZATION
          ? this.routes.sportOrganizer.ticketsSport
          : this.routes.organizer.tickets
      this.goTo(routeName)
    },
    onSendMessageToAttendeesClick() {
      const routeName =
        this.organizationType === OrganizationType.THEATER
          ? this.routes.theaterOrganizer.createEmailTheater
          : this.organizationType === OrganizationType.SPORT_ORGANIZATION
            ? this.routes.sportOrganizer.createEmailSport
            : this.routes.organizer.createEmail
      this.goTo(routeName, { eventId: this.eventId, initiallyAllAttendees: true })
    },
    async updateEventCover(cover) {
      const mutation =
        this.organizationType === OrganizationType.THEATER
          ? UPDATE_EVENT_TEMPLATE_COVER
          : UPDATE_EVENT_COVER
      const identifier =
        this.organizationType === OrganizationType.THEATER
          ? { organizationSlug: this.organizationSlug, eventTemplateId: this.eventTemplateId }
          : { eventId: this.eventId }
      const { errors } = await this.$apollo.mutate({
        mutation,
        variables: {
          ...identifier,
          cover,
        },
      })

      if (errors) {
        this.message = 'navigation.error_in_updating_event_cover'
        this.color = 'error'
        this.snackbar = true

        return
      }

      this.openAddEventCoverModal = false
      this.event.cover = cover
      this.message = 'navigation.cover_successfully_updated'
      this.color = 'primary'
      this.snackbar = true
    },
    onChangeEventStatusLabelClick() {
      if (this.changeEventStatusLabel === 'Change status') {
        this.eventStatusMenuOpen = true

        return
      }

      this.onChangeEventStatus(this.changeEventStatusLabel)
    },
    async onSchedulePublish(publishDate, publishTime) {
      const {
        errors,
        data: {
          updateEventScheduledPublishDate: { scheduledPublishDate },
        },
      } = await this.$apollo.mutate({
        mutation: UPDATE_EVENT_SCHEDULED_PUBLISH_DATE,
        variables: {
          eventId: this.eventId,
          date: moment(`${publishDate} ${publishTime}`),
        },
      })

      if (errors) {
        this.message = 'navigation.error_in_scheduling_publish'
        this.color = 'error'
        this.snackbar = true

        return
      }

      this.openSchedulePublishModal = false
      this.event.scheduledPublishDate = scheduledPublishDate
      this.message = 'navigation.schedule_success'
      this.color = 'primary'
      this.snackbar = true
    },
    findPerformanceSlugWithEarliestFromDate(performances) {
      if (!performances || !performances.length) {
        return ''
      }
      const earliestPerformance = minBy(performances, performance => new Date(performance.from))

      return earliestPerformance.slug
    },
    async theaterPublishClick() {
      if (!this.event.cover) {
        this.openAddEventCoverModal = true

        return
      }
      await this.publishEventPage()
    },
  },
  // apollo: {
  //   getEventAndUser: {
  //     query: GET_EVENT_AND_LOGGED_USER,
  //     variables() {
  //       return {
  //         id: this.eventId,
  //       }
  //     },
  //     manual: true,
  //     // error() {
  //     //   this.goTo(this.routes.organizer.organizations)
  //     // },
  //     result({ data, loading }) {
  //       console.log(data, 'aaa')
  //       if (!loading && data) {
  //         this.me = data.me
  //         this.event = data.getEvent
  //         this.requestCount = data.getPendingRequestsCount
  //         this.eventSlug = data.getEvent.slug
  //         this.eventTitle = data.getEvent.title
  //         this.eventCode = data.getEvent.eventCode
  //         this.dateAndTime = moment(data.getEvent.from).local('en')
  //         this.location = data.getEvent.location.venue
  //
  //         this.sidebarConfig = organizerSidebarConfiguration
  //           .filter(route => this.shouldRender(route))
  //           .map(route => {
  //             if (route.subroutes) {
  //               route.subroutes = route.subroutes.filter(subroute => this.shouldRender(subroute))
  //             }
  //
  //             return route
  //           })
  //       }
  //     },
  //   },
  // },
}
</script>
<style scoped>
.main-page {
  display: grid;
  grid-template-columns: 22rem auto;
}

.sidebar-container {
  position: sticky;
  top: 5rem;
}

.sidebar-wrapper {
  grid-column: 1/2;
}

.screens {
  grid-column: 2/3;
  padding: 0 2rem;
  box-sizing: border-box;
}

.sidebar-header {
  border-radius: 0 0.75rem 0.75rem 0;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.sidebar-header-back {
  display: flex;
  align-items: center;
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  font-size: 1rem;
  line-height: 120%;
  color: #ff4c00;
  margin-bottom: 1rem;
}

.back-icon {
  color: #0277bd !important;
  background-color: inherit;
}

.go-back-container {
  background-color: white;
  border-radius: 1.5rem;
  width: 3rem;
  height: 3rem;
}

.sidebar-header-title {
  color: #0277bd;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.0375rem;
  margin-bottom: 0.25rem;
  word-break: break-word;
  hyphens: auto;
}

.sidebar-header-subtitle {
  color: #0277bd;
  font-family: 'Source Sans Pro';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  margin-bottom: 1rem;
}

.sidebar-view-event-btn {
  height: 48px;
  padding: 12px 24px;
  gap: 8px;
  min-width: 226px;
  font-family: 'Raleway', 'sans-serif';
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 120%;
  text-transform: uppercase;
  background: #0277bd;
  border-radius: 1.5rem;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: #fff;
  width: fit-content;
  cursor: pointer;
}

.sidebar-view-event-more {
  font-family: 'Raleway', 'sans-serif';
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 120%;
  text-transform: uppercase;
  background: #fff;
  border-radius: 1.5rem;
  display: inline-flex;
  padding: 1.12rem 1.12rem;
  justify-content: center;
  align-items: center;
  color: #0277bd;
  width: fit-content;
  cursor: pointer;
  margin-left: 1rem;
}

.publish-page {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.publish-row {
  display: flex;
  gap: 0.75rem;
}

.view-page {
  display: flex;
  width: 3rem;
  height: 3rem;
  padding: 0.75rem;
  justify-content: center;
  align-items: center;
  border-radius: 1.5rem;
  background: #fff;
  color: #0277bd;
  cursor: pointer;
}

.more-options-button {
  display: flex;
  width: 3rem;
  height: 3rem;
  padding: 0.75rem;
  justify-content: center;
  align-items: center;
  border-radius: 1.5rem;
  background: #fff;
  color: #0277bd;
  cursor: pointer;
  margin-top: 1rem;
}

.sidebar-view-event {
  font-family: 'Raleway', 'sans-serif';
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 120%;
  border: 2px solid var(--primary-10, #0277bd);
  text-transform: uppercase;
  background: transparent;
  border-radius: 1.5rem;
  display: inline-flex;
  padding: 0.6rem 1.5rem;
  justify-content: center;
  align-items: center;
  color: #0277bd;
  width: fit-content;
  cursor: pointer;
  margin-top: 1rem;
}

.view-icon {
  margin-left: 0.2rem;
  filter: invert(30%) sepia(56%) saturate(2324%) hue-rotate(180deg) brightness(94%) contrast(98%);
  transform: rotate(45deg); /* Rotate the image 90 degrees (horizontal) */
}

.publish-icon {
  margin-left: 0.2rem;
}

.overlay {
  position: fixed;
  top: 17rem;
  left: 23.125rem;
  background-color: #fff;
  overflow: auto;
  z-index: 99;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 0.5rem;
  box-shadow: 0px 4px 16px 0px rgba(51, 51, 51, 0.16);
}

.menu-item {
  display: flex;
  width: 16.375rem;
  padding: 0.75rem;
  align-items: center;
  gap: 0.75rem;
  color: #0277bd;
  font-family: Raleway;
  font-size: 1rem;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.24px;
  cursor: pointer;
}

.scanner-code-container {
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.scanner-code-container span {
  overflow: hidden;
  color: #0277bd;
  text-overflow: ellipsis;
  font-family: 'Source Sans Pro';
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.add-more-tickets-container {
  display: flex;
  padding: 0.5rem 1rem 1rem 1rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  align-self: stretch;
  margin-bottom: 1rem;

  border-radius: 0.5rem;
  background: #e6f1f8;
  mix-blend-mode: multiply;
}

.add-more-tickets-container p {
  color: #0277bd;
  text-align: center;
  margin: 0;

  font-family: 'Source Sans Pro';
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.share-change-status-container {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.share-event-page-button {
  margin: 0;
  min-height: 3rem;
  height: auto;
  max-width: 100%;
  padding: 12px 16px;
  background-color: white;
}
.share-event-page-button >>> .v-btn__content {
  flex: 1 1 auto;
  white-space: normal;
}

.status-change-button {
  display: flex;
  position: relative;
  align-items: flex-start;
  gap: 0.125rem;
  flex: 1 0 0;
  border-radius: 1.5rem;
  min-height: 36px;
}

.change-status-label {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  flex: 1 0 0;
  background: white;
  height: 100%;
  border-radius: 1.5rem 0 0 1.5rem;

  color: #0277bd;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Raleway;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.01688rem;
  text-transform: uppercase;
}

.change-status-label--selected {
  color: white;
  background: #0277bd;
}

.change-status-expand {
  display: flex;
  width: 3rem;
  height: 100%;
  padding: 0 0.625rem 0 0.375rem;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border-radius: 0 1.5rem 1.5rem 0;
}

.change-status-expand--selected {
  background: #0277bd;
}

::v-deep .v-menu__content {
  max-width: 100%;
  width: 100%;
}

@media only screen and (max-width: 800px) {
  .main-page {
    grid-template-columns: 100%;
    padding: 0;
  }

  .sidebar-wrapper {
    grid-column: 1;
    position: relative;
    margin: 0 -24px;
  }

  .sidebar-nav-container {
    background-color: #f5f5f5;
  }

  .sidebar-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.125rem 1.5rem;
  }

  .sidebar-nav-title {
    font-family: 'Raleway', sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 1.5rem;
    line-height: 120%;
    letter-spacing: -0.03em;
    font-feature-settings: 'pnum' on, 'lnum' on;
    color: #ff4c00;
  }

  .sidebar-container {
    margin: 0;
  }

  .sidebar-holder {
    width: 100%;
  }

  .sidebar-header {
    border-radius: 2rem;
  }

  .screens {
    grid-column: 1;
    width: 100%;
    padding: 0;
  }

  .overlay {
    top: 14rem;
    left: 9rem;
  }
}
</style>
